import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'

import { PASSWORD_HINT, passwordProblem } from '@/lib/passwordPolicy'

import { Icon } from '@/icons/Icon'

import { message, pPost, type PlatformMe } from './api'
import { usePlatformAuth } from './auth'
import { hashFor } from './handover'

/**
 * Change your own password.
 *
 * The only write the API allows while `mustChangePassword` is set, and the
 * portal routes a temporary account here rather than letting it collect 403s.
 * The strength rules run in this browser, because the server never sees a
 * password - it receives PBKDF2 over it and could not judge one if it wanted
 * to (`frontend/src/lib/passwordPolicy.ts`).
 */
export default function PlatformPasswordPage() {
  const { me, setMe } = usePlatformAuth()
  const navigate = useNavigate()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')

  const email = me?.admin.email ?? ''
  const problem = next ? passwordProblem(next, email) : null
  const mismatch = again.length > 0 && next !== again

  const save = useMutation({
    mutationFn: async () =>
      pPost<PlatformMe>('/auth/password', {
        currentPassword: await hashFor(current, email),
        password: await hashFor(next, email),
      }),
    onSuccess: (updated) => {
      setMe(updated)
      navigate('/admin/organizations', { replace: true })
    },
  })

  const ready = current && next && next === again && !problem

  return (
    <>
      <div className="pf-head">
        <div>
          <h1 className="pf-head__h">
            {me?.admin.mustChangePassword ? 'Choose your password' : 'Change your password'}
          </h1>
          <p className="pf-head__d">
            {me?.admin.mustChangePassword
              ? 'Somebody else set the one you signed in with.'
              : 'Ends every other session this account has.'}
          </p>
        </div>
      </div>

      <section className="st-panel pf-sec" style={{ maxWidth: 520 }}>
        <form
          className="st-panel__body"
          onSubmit={(e) => {
            e.preventDefault()
            if (save.isPending || !ready) return
            save.mutate()
          }}
        >
          <div className="st-stack">
            <label className="st-field">
              <span>Current password</span>
              <input
                className="ix-input"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                autoFocus
                required
              />
            </label>

            <label className="st-field">
              <span>New password</span>
              <input
                className="ix-input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
              />
              <small>{problem ?? PASSWORD_HINT}</small>
            </label>

            <label className="st-field">
              <span>New password again</span>
              <input
                className="ix-input"
                type="password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                autoComplete="new-password"
                required
              />
              {mismatch ? <small style={{ color: 'var(--ix-error)' }}>These do not match.</small> : null}
            </label>
          </div>

          {save.error ? (
            <div className="ix-alert ix-alert--error" role="alert" style={{ marginTop: 14 }}>
              <div>{message(save.error, 'Could not change the password.')}</div>
            </div>
          ) : null}

          <div className="st-form__actions">
            <button className="ix-btn ix-btn--primary" type="submit" disabled={save.isPending || !ready}>
              {save.isPending ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      </section>

      <TwoFactor />
    </>
  )
}


/**
 * Two-factor for the portal.
 *
 * A platform administrator can create, switch off and delete every carrier on
 * the installation, so if any account on this platform warrants a second
 * factor it is this one. The flow is the carrier's, because there is one
 * correct way to do this and it is already written (`app/core/mfa.py`): a
 * secret to scan, a code to confirm it, and recovery codes shown once.
 */
function TwoFactor() {
  const { me, refresh } = usePlatformAuth()
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string; qrCode: string } | null>(null)
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [password, setPassword] = useState('')
  const [disabling, setDisabling] = useState(false)

  const on = me?.admin.mfaEnabled === true

  const begin = useMutation({
    mutationFn: () => pPost<{ secret: string; otpauthUri: string; qrCode: string }>('/auth/mfa/setup'),
    onSuccess: setSetup,
  })
  const enable = useMutation({
    mutationFn: () => pPost<{ recoveryCodes: string[] }>('/auth/mfa/enable', { code: code.replace(/\s/g, '') }),
    onSuccess: async (r) => {
      setCodes(r.recoveryCodes)
      setSetup(null)
      setCode('')
      await refresh()
    },
  })
  const disable = useMutation({
    mutationFn: async () =>
      pPost('/auth/mfa/disable', {
        password: await hashFor(password, me?.admin.email ?? ''),
        code: code.replace(/\s/g, ''),
      }),
    onSuccess: async () => {
      setDisabling(false)
      setPassword('')
      setCode('')
      await refresh()
    },
  })

  return (
    <section className="st-panel pf-sec" style={{ maxWidth: 520, marginTop: 18 }}>
      <header className="st-panel__head">
        <div>
          <div className="st-panel__t">Two-factor authentication</div>
          <div className="st-panel__d">
            {on ? "On. You'll be asked for a code at every sign-in." : 'An app code as well as your password.'}
          </div>
        </div>
        <span className={on ? 'ix-chip ix-chip--success' : 'ix-chip ix-chip--neutral'}>
          {on ? 'On' : 'Off'}
        </span>
      </header>

      <div className="st-panel__body">
        {codes ? (
          <>
            <div className="ix-alert ix-alert--warning" role="status">
              <div>
                <strong>Recovery codes. Save them now.</strong> Each works once, and they are not
                shown again.
              </div>
            </div>
            <pre className="pf-codes">{codes.join('\n')}</pre>
            <div className="st-form__actions">
              <button className="ix-btn ix-btn--primary" type="button" onClick={() => setCodes(null)}>
                I have saved them
              </button>
            </div>
          </>
        ) : on ? (
          disabling ? (
            <form
              className="st-stack"
              onSubmit={(e) => {
                e.preventDefault()
                if (!disable.isPending && password && code.trim()) disable.mutate()
              }}
            >
              <label className="st-field">
                <span>Your password</span>
                <input className="ix-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
              <label className="st-field">
                <span>A current code</span>
                <input className="ix-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={12} required />
                <small>Turning it off costs the same proof turning it on did.</small>
              </label>
              {disable.error ? <div className="ix-error-text">{message(disable.error)}</div> : null}
              <div className="st-form__actions">
                <button className="st-act st-act--danger" type="submit" disabled={disable.isPending}>
                  {disable.isPending ? 'Turning off…' : 'Turn off'}
                </button>
                <button className="ix-btn ix-btn--secondary" type="button" onClick={() => setDisabling(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button className="ix-btn ix-btn--secondary" type="button" onClick={() => setDisabling(true)}>
              Turn off
            </button>
          )
        ) : setup ? (
          <form
            className="st-stack"
            onSubmit={(e) => {
              e.preventDefault()
              if (!enable.isPending && code.trim()) enable.mutate()
            }}
          >
            <img src={setup.qrCode} alt="Scan this with your authenticator app" width={176} height={176} />
            <div className="ix-caption ix-text-muted">
              Can&rsquo;t scan? Enter this key: <code className="pf-mono">{setup.secret}</code>
            </div>
            <label className="st-field">
              <span>Code from the app</span>
              <input className="ix-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={12} autoFocus required />
            </label>
            {enable.error ? <div className="ix-error-text">{message(enable.error)}</div> : null}
            <div className="st-form__actions">
              <button className="ix-btn ix-btn--primary" type="submit" disabled={enable.isPending || !code.trim()}>
                {enable.isPending ? 'Confirming…' : 'Confirm'}
              </button>
              <button className="ix-btn ix-btn--secondary" type="button" onClick={() => setSetup(null)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            {begin.error ? <div className="ix-error-text">{message(begin.error)}</div> : null}
            <button className="ix-btn ix-btn--primary" type="button" disabled={begin.isPending} onClick={() => begin.mutate()}>
              <Icon name="shield" size={14} />
              {begin.isPending ? 'Starting…' : 'Turn on'}
            </button>
          </>
        )}
      </div>
    </section>
  )
}
